import AttachmentDto from '../../../../types/AttachmentDto';
import { LegacySectionOrArtificialSection } from '../../../../types/LegacyReviewQuestionnaire';
import MediumUser from '../../../../types/MediumUser';
import MiniUser from '../../../../types/MiniUser';

export type QuestionPayload = {
    description: string;
    characterLimit: number;
    ratings: Object[];
    sliderSteps: number;
    sliderLabels: [string, string];
    allowComment: boolean;
};

// TODO: Create specific answer subtypes.
export type AnswerPayload = {
    text?: string;
    sliderValue?: string | null;
    code?: string;
    posX?: number;
    posY?: number;
    comment?: string;
};

export type ReviewPermissions = {
    canWrite: boolean;
    canShare: boolean;
    canHide: boolean;
    canEditOverallRating: boolean;
    canEditSecondaryOverallRating: boolean;
    canReviewerShare: boolean;
    canReviewerSign: boolean;
    canReviewerEditOverallRating: boolean;
    canReviewerEditSecondaryOverallRating: boolean;
    reasonNotViewable: string;
    reasonNotEditable: string;
    reasonNotShareable: string;
    reasonNotSignable: string;
};

export enum ReviewStatus {
    NOT_STARTED = 'NOT_STARTED',
    STARTED = 'STARTED',
    WRITTEN = 'WRITTEN',
    SHARED = 'SHARED',
    SIGNED = 'SIGNED',
}

type ReviewRatingDefinition = {
    code: string;
    name: string;
    description: string;
};

type ReviewGraphDefinition = {
    xDescription: string;
    xLabel: string;
    xLow: string;
    xHigh: string;
    yDescription: string;
    yLabel: string;
    yLow: string;
    yHigh: string;
};

export type Review = {
    id: string;
    cycleName: string;
    cycleIntroduction: string;
    underReviewFrom: string;
    underReviewTo: string;
    complete: boolean;
    createdAt: string;
    timeline: Timeline;
    sharingAndApproval: SharingAndApproval;
    questionOrder: string[];
    permissions: ReviewPermissions;
    reviewee: { user: MiniUser };
    mainReviewer?: MiniUser;
    contributingReviewers: MiniUser[];
    ratingDefinition: ReviewRatingDefinition[];
    graphDefinition: ReviewGraphDefinition;
    assessmentToWriteType: AssessmentType;
    revieweesAttachments: AttachmentDto[];
    reviewersAttachments: AttachmentDto[];
    mainReviewerManager?: MiniUser;
    customMainReviewer: boolean;
    useIndividualAssessments: boolean;
};

export type Timeline = {
    editableAfter: string | null;
    writeByReviewee: string;
    writeByReviewer: string;
    editableUntilReviewees: string | null;
    editableUntilReviewers: string | null;
    reviewerShareFrom: string | null;
    discussAndShareBy: string | null;
    signBy: string;
    managerRatingsLockBy: string | null;
};

export type SharingAndApproval = {
    managerMustShareToSee: boolean;
    employeeMustShareToSee: boolean;
    hrAndIndirectManagerApproval: string;
    allowIndirectManagersToEdit: boolean;
    notifyHrAdminsWhenManagersAreFinishedWriting: boolean;
    notifyIndirectManagersWhenManagersAreFinishedWriting: boolean;
};

export type Answer = {
    questionId: string;
    author: MiniUser;
    questionPayload: QuestionPayload;
    answerPayload: AnswerPayload;
    type:
        | 'GRAPH_RATING'
        | 'RATING' /* overall rating */
        | 'SECONDARY_RATING'
        | 'TEXT'
        | 'TEXT_WITH_RATING'
        | 'SCALES'
        | 'HEADING'
        | 'SLIDER'
        | 'CHECKBOXES'
        | 'DROPDOWN';
};

export enum AssessmentStatus {
    NOT_STARTED = 'NOT_STARTED',
    STARTED = 'STARTED',
    WRITTEN = 'WRITTEN',
    SHARED = 'SHARED',
    SIGNED = 'SIGNED',
}

export enum AssessmentType {
    SELF_ASSESSMENT = 'SELF_ASSESSMENT',
    ASSESSMENT = 'ASSESSMENT',
    CONTRIBUTOR_ASSESSMENT = 'CONTRIBUTOR_ASSESSMENT',
}

export type Assessment = {
    id: string;
    reviewId: string;
    author: MiniUser;
    answers: Answer[];
    attachments: AttachmentDto[];
    assessmentStatus: AssessmentStatus;
    type: AssessmentType;
};

export enum BlockReason {
    REVIEW_SIGNED_OR_COMPLETE = 'REVIEW_SIGNED_OR_COMPLETE',
    IS_REVIEWEE = 'IS_REVIEWEE',
    IS_MAIN_REVIEWER = 'IS_MAIN_REVIEWER',
    CALIBRATION_STATE_BLOCKED = 'CALIBRATION_STATE_BLOCKED',
    IS_INDIRECT_MANAGER = 'IS_INDIRECT_MANAGER',
    TIMELINE_ENDED = 'TIMELINE_ENDED',
    NOT_ALLOWED = 'NOT_ALLOWED',
}

// see ReviewListDto.java
export type LegacyReview = {
    id: string;
    cycleId: string;

    permissions: {
        canNudgeManager: boolean;
        canNudgeSecondaryReviewer: boolean;
        canNudgeReviewee: boolean;
        canInformManager: boolean;
        canInformSecondaryReviewer: boolean;
        canInformReviewee: boolean;
        canPeek: boolean;
        canEdit: boolean;
        canDelete: boolean;
        canSeeAdminActions: boolean;
        canSeeManagerEvaluation: boolean;
        canEditManagerRating: boolean;
        canEditSecondaryOverallRating: boolean;
        canEditRatingOutsideTimeline: { allowed: boolean; reason: BlockReason };
        canSeeSelfAssessment: boolean;
        canSeeReviewContent: boolean;
        canSignReviewee: boolean;
        canSignManager: boolean;
        canRevokeManagerSign: boolean;
        canRevokeRevieweeSign: boolean;
        canComplete: boolean;
        canReopen: boolean;
        canMove: boolean;
        canShareWithReviewee: boolean;
        canHideFromReviewee: boolean;
        canHideFromManager: boolean;
        canWriteForOtherReviewers: boolean;
    };

    complete: boolean;

    reviewee: MediumUser;
    revieweeId: string;
    revieweeStatus: 'Signed' | 'Shared' | 'Started' | 'Not started';
    revieweeRatingCode: string | null;
    revieweeRatingCodeSecondary: string | null;
    revieweeNudgedDate: number | null;
    revieweeRatingName: string | null;
    revieweeRatingNameSecondary: string | null;

    manager: MiniUser | null;
    managerId: string | null;
    managerStatus: 'Signed' | 'Shared' | 'Written' | 'Started' | 'Not started';
    managerRatingCode: string | null;
    managerRatingCodeSecondary: string | null;
    managerNudgedDate: number | null;
    managerRatingName: string | null;
    managerRatingNameSecondary: string | null;

    secondaryReviewer: MiniUser | null;

    isSharingWithManager: boolean;
    isSharingWithReviewee: boolean;
    isEditedByReviewee: boolean;
    isEditedByManager: boolean;

    contributingReviewers: MiniUser[];

    revieweeSignNote: string | null;
    reviewerSignNote: string | null;

    revieweeEditExtension: string | null;
    reviewerEditExtension: string | null;

    ratingAnswers: {
        reviewee: ReviewRatingAnswerDto[] | null;
        reviewer: ReviewRatingAnswerDto[] | null;
    };
};

export type ReviewRatingAnswerDto = {
    rating: number | null;
    choices: boolean[];
};

export type ReviewCycleGuidedTimeline = {
    processStarts: Date;
    revieweeWriteUntil: Date;
    managerWriteUntil: Date;
    managerShareFrom: Date;
    managerShareUntil: Date;
    managerRatingsLockBy: Date;
    discussAndShareBy: Date;
    signBy: Date;
    processEndsForReviewees: Date;
    processEndsForReviewers: Date;
};

type ReviewCycleGuidedApprovalProcess = 'NOT_NECESSARY' | 'ALLOWED';

export type CycleType = 'CALIBRATION' | 'SIMPLE';

export type ReviewQuestionnaireTemplate = {
    id: string;
    title: string;
    description: string;
    sections: LegacySectionOrArtificialSection[];
};

export type ReviewCycleGuidedModel = {
    cycleType: CycleType;
    timeline: ReviewCycleGuidedTimeline;
    scheduleInformOnCycleStart: boolean;
    allowContentChangesWhileSharing: boolean;
    allowContentChangesWhileCalibrating: boolean;
    allowOverallRatingChangesWhileSharing: boolean;
    allowOverallRatingChangesWhileCalibrating: boolean;
    sharingAndApproval: {
        hrAndIndirectManagerApproval: ReviewCycleGuidedApprovalProcess;
    };
};

export type ReviewCycleGuided = {
    model: ReviewCycleGuidedModel;
};

export interface LegacyReviewMini {
    id: string;
    reviewee: MiniUser;
    manager: MiniUser;
    contributingReviewers: MiniUser[];
    revieweeStatus: ReviewStatus;
    managerStatus: ReviewStatus;
    revieweeEditDate: number;
    managerEditDate: number;
    revieweeSignDate: number;
    managerSignDate: number;
}
